import React, { useEffect, useState, useMemo } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "../components/ui/dialog";
import { toast, Toaster } from "sonner";
import { fmtMoney, exportToPDF, exportToExcel } from "../lib/exports";
import { Plus, Trash2, Edit, FileDown, FileSpreadsheet, Globe2, Building2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

export default function GastosOperativos() {
  const { user } = useAuth();
  const puedeEditar = canEdit(user, "gastos_operativos");

  const [fechaIni, setFechaIni] = useState(monthStart());
  const [fechaFin, setFechaFin] = useState(today());
  const [sucursalFilter, setSucursalFilter] = useState("");
  const [categoria, setCategoria] = useState("");
  const [metodoPago, setMetodoPago] = useState("");

  const [sucursales, setSucursales] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/sucursales"), api.get("/gastos/categorias")]).then(([rs, rc]) => {
      setSucursales(rs.data); setCategorias(rc.data);
    });
  }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const params = { fecha_ini: fechaIni, fecha_fin: fechaFin, tipo: "operativo" };
      if (sucursalFilter === "global") params.sucursal_id = "__global__"; // marker handled below
      else if (sucursalFilter) params.sucursal_id = sucursalFilter;
      if (categoria) params.categoria = categoria;
      if (metodoPago) params.metodo_pago = metodoPago;
      const { data } = await api.get("/gastos", { params });
      // Cliente filtra globales si selecciona "global"
      let filtered = data;
      if (sucursalFilter === "global") {
        filtered = data.filter((g) => !g.sucursal_id);
      }
      setRows(filtered);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { cargar(); }, []);

  const sucName = (id) => id ? (sucursales.find((s) => s.id === id)?.nombre || "—") : "Global";

  const total = useMemo(() => rows.reduce((s, r) => s + (r.importe || 0), 0), [rows]);

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar gasto operativo?")) return;
    try { await api.delete(`/gastos/${id}`); toast.success("Eliminado"); cargar(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const expPDF = () => {
    const data = rows.map((r) => ({
      ...r,
      sucursal: sucName(r.sucursal_id),
      importe_fmt: fmtMoney(r.importe),
      metodo: r.metodo_pago || "efectivo",
    }));
    exportToPDF({
      title: "Gastos Operativos",
      subtitle: `${fechaIni} a ${fechaFin}`,
      columns: [
        { header: "Fecha", key: "fecha" },
        { header: "Alcance", key: "sucursal" },
        { header: "Categoría", key: "categoria" },
        { header: "Método", key: "metodo" },
        { header: "Comentario", key: "comentario" },
        { header: "Importe", key: "importe_fmt" },
      ],
      rows: data,
      summary: [{ label: "Total", value: fmtMoney(total) }, { label: "Registros", value: rows.length }],
    });
  };
  const expXLS = () => {
    exportToExcel({
      title: "Gastos_Operativos",
      rows: rows.map((r) => ({
        Fecha: r.fecha,
        Alcance: sucName(r.sucursal_id),
        Categoría: r.categoria,
        Método: r.metodo_pago || "efectivo",
        Importe: r.importe,
        Comentario: r.comentario,
        Usuario: r.usuario_email,
      })),
    });
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Administración</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">Gastos Operativos</h1>
          <p className="text-sm text-slate-500 mt-1">
            Gastos administrativos. Asigna a una <strong>sucursal específica</strong> (renta, luz, agua) o márcalos como <strong>Global</strong> (compras centralizadas como pollo crudo).
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={expPDF}><FileDown className="w-4 h-4 mr-2" />PDF</Button>
          <Button variant="outline" onClick={expXLS}><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</Button>
          {puedeEditar && (
            <Dialog open={showNew} onOpenChange={setShowNew}>
              <DialogTrigger asChild>
                <Button data-testid="gop-nuevo" className="bg-blue-600 hover:bg-blue-700 text-white"><Plus className="w-4 h-4 mr-2" />Nuevo Gasto</Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader><DialogTitle>Nuevo Gasto Operativo</DialogTitle></DialogHeader>
                <GastoOperativoForm sucursales={sucursales} categorias={categorias} onSaved={() => { setShowNew(false); cargar(); }} onCancel={() => setShowNew(false)} />
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card className="p-5 grid grid-cols-2 md:grid-cols-6 gap-3">
        <Field label="Desde"><Input type="date" value={fechaIni} onChange={(e) => setFechaIni(e.target.value)} className="bg-blue-50 border-blue-200" /></Field>
        <Field label="Hasta"><Input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="bg-blue-50 border-blue-200" /></Field>
        <Field label="Alcance">
          <Select value={sucursalFilter || "all"} onValueChange={(v) => setSucursalFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="bg-blue-50 border-blue-200"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="global">Solo Globales</SelectItem>
              {sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Categoría">
          <Select value={categoria || "all"} onValueChange={(v) => setCategoria(v === "all" ? "" : v)}>
            <SelectTrigger className="bg-blue-50 border-blue-200"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {categorias.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Método">
          <Select value={metodoPago || "all"} onValueChange={(v) => setMetodoPago(v === "all" ? "" : v)}>
            <SelectTrigger className="bg-blue-50 border-blue-200"><SelectValue placeholder="Todos" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos</SelectItem>
              <SelectItem value="efectivo">Efectivo</SelectItem>
              <SelectItem value="transferencia">Transferencia</SelectItem>
              <SelectItem value="tarjeta">Tarjeta</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <div className="flex items-end">
          <Button onClick={cargar} className="bg-blue-600 hover:bg-blue-700 text-white w-full h-10">Filtrar</Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr>
                <Th>Fecha</Th><Th>Alcance</Th><Th>Categoría</Th><Th>Método</Th>
                <Th>Comentario</Th><Th>Usuario</Th><Th right>Importe</Th>
                {puedeEditar && <Th>Acciones</Th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Cargando…</td></tr>}
              {!loading && !rows.length && <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">Sin registros</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <Td>{r.fecha}</Td>
                  <Td>
                    {r.sucursal_id ? (
                      <span className="inline-flex items-center gap-1 text-slate-700"><Building2 className="w-3.5 h-3.5 text-slate-500" />{sucName(r.sucursal_id)}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-blue-700 font-medium"><Globe2 className="w-3.5 h-3.5" />Global</span>
                    )}
                  </Td>
                  <Td>{r.categoria}</Td>
                  <Td><MetodoBadge metodo={r.metodo_pago || "efectivo"} /></Td>
                  <Td className="text-xs text-slate-500">{r.comentario}</Td>
                  <Td className="text-xs text-slate-500">{r.usuario_email}</Td>
                  <Td right mono className="font-semibold">{fmtMoney(r.importe)}</Td>
                  {puedeEditar && (
                    <Td>
                      <Button variant="ghost" size="icon" onClick={() => setEditing(r)}><Edit className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => eliminar(r.id)} className="text-red-600"><Trash2 className="w-4 h-4" /></Button>
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-slate-900 text-white">
                <tr>
                  <td className="px-4 py-3 font-semibold" colSpan={6}>TOTAL</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-lg">{fmtMoney(total)}</td>
                  {puedeEditar && <td></td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Editar Gasto Operativo</DialogTitle></DialogHeader>
          {editing && <GastoOperativoForm gasto={editing} sucursales={sucursales} categorias={categorias} onSaved={() => { setEditing(null); cargar(); }} onCancel={() => setEditing(null)} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function GastoOperativoForm({ gasto, sucursales, categorias, onSaved, onCancel }) {
  const isEdit = !!gasto;
  const [form, setForm] = useState({
    fecha: gasto?.fecha || today(),
    alcance: gasto ? (gasto.sucursal_id ? "sucursal" : "global") : "global",
    sucursal_id: gasto?.sucursal_id || "",
    categoria: gasto?.categoria || "",
    importe: gasto?.importe ?? "",
    metodo_pago: gasto?.metodo_pago || "transferencia",
    comentario: gasto?.comentario || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const guardar = async () => {
    if (!form.categoria) return toast.error("Selecciona categoría");
    if (!form.importe || parseFloat(form.importe) <= 0) return toast.error("Importe inválido");
    if (form.alcance === "sucursal" && !form.sucursal_id) return toast.error("Selecciona sucursal");

    setBusy(true);
    try {
      const payload = {
        fecha: form.fecha,
        sucursal_id: form.alcance === "global" ? null : form.sucursal_id,
        categoria: form.categoria,
        importe: parseFloat(form.importe),
        comentario: form.comentario,
        metodo_pago: form.metodo_pago,
        tipo: "operativo",
      };
      if (isEdit) await api.put(`/gastos/${gasto.id}`, payload);
      else await api.post("/gastos", payload);
      toast.success("Guardado");
      onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Fecha</Label>
          <Input type="date" value={form.fecha} onChange={(e) => set("fecha", e.target.value)} className="mt-1.5 bg-blue-50 border-blue-200" data-testid="gop-fecha" />
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Método de pago</Label>
          <Select value={form.metodo_pago} onValueChange={(v) => set("metodo_pago", v)}>
            <SelectTrigger className="mt-1.5 bg-blue-50 border-blue-200" data-testid="gop-metodo"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="efectivo">Efectivo</SelectItem>
              <SelectItem value="transferencia">Transferencia</SelectItem>
              <SelectItem value="tarjeta">Tarjeta</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Alcance del gasto</Label>
        <div className="mt-1.5 grid grid-cols-2 gap-2">
          <button
            type="button"
            onClick={() => set("alcance", "global")}
            data-testid="gop-alcance-global"
            className={`border rounded-md p-3 text-left text-sm transition-colors ${
              form.alcance === "global" ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <div className="flex items-center gap-2 font-semibold">
              <Globe2 className="w-4 h-4 text-blue-600" /> Global
            </div>
            <div className="text-xs text-slate-500 mt-1">Aplica a toda la operación (ej. compra de pollo crudo)</div>
          </button>
          <button
            type="button"
            onClick={() => set("alcance", "sucursal")}
            data-testid="gop-alcance-sucursal"
            className={`border rounded-md p-3 text-left text-sm transition-colors ${
              form.alcance === "sucursal" ? "border-blue-500 bg-blue-50 ring-2 ring-blue-200" : "border-slate-200 hover:border-slate-300"
            }`}
          >
            <div className="flex items-center gap-2 font-semibold">
              <Building2 className="w-4 h-4 text-slate-700" /> Sucursal específica
            </div>
            <div className="text-xs text-slate-500 mt-1">Renta, luz, agua, etc. de una sucursal</div>
          </button>
        </div>
      </div>

      {form.alcance === "sucursal" && (
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Sucursal</Label>
          <Select value={form.sucursal_id} onValueChange={(v) => set("sucursal_id", v)}>
            <SelectTrigger className="mt-1.5 bg-blue-50 border-blue-200" data-testid="gop-sucursal"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
            <SelectContent>
              {sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Categoría</Label>
          <Select value={form.categoria} onValueChange={(v) => set("categoria", v)}>
            <SelectTrigger className="mt-1.5 bg-blue-50 border-blue-200" data-testid="gop-categoria"><SelectValue placeholder="Selecciona…" /></SelectTrigger>
            <SelectContent>
              {categorias.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Importe</Label>
          <Input type="number" min="0" step="0.01" inputMode="decimal"
            value={form.importe} onChange={(e) => set("importe", e.target.value)}
            data-testid="gop-importe"
            className="mt-1.5 bg-blue-50 border-blue-200 font-mono" />
        </div>
      </div>

      <div>
        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">Comentario</Label>
        <Textarea value={form.comentario} onChange={(e) => set("comentario", e.target.value)} className="mt-1.5 bg-blue-50 border-blue-200" rows={2} placeholder="Opcional" />
      </div>

      <DialogFooter className="pt-2">
        <Button variant="outline" onClick={onCancel}>Cancelar</Button>
        <Button onClick={guardar} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="gop-guardar">{busy ? "Guardando…" : "Guardar"}</Button>
      </DialogFooter>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const Th = ({ children, right }) => <th className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 ${right ? "text-right" : ""}`}>{children}</th>;
const Td = ({ children, right, mono, className = "" }) => <td className={`px-4 py-2.5 text-sm ${right ? "text-right" : ""} ${mono ? "font-mono" : ""} ${className}`}>{children}</td>;
const MetodoBadge = ({ metodo }) => {
  const styles = {
    efectivo: "bg-emerald-100 text-emerald-800",
    transferencia: "bg-blue-100 text-blue-800",
    tarjeta: "bg-purple-100 text-purple-800",
  };
  const labels = { efectivo: "Efectivo", transferencia: "Transferencia", tarjeta: "Tarjeta" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${styles[metodo] || styles.efectivo}`}>
      {labels[metodo] || metodo}
    </span>
  );
};
